if (!customElements.get("orthora-pdp-gallery")) {
  customElements.define(
    "orthora-pdp-gallery",
    class OrthoraPdpGallery extends HTMLElement {
      connectedCallback() {
        if (this.galleryInitialized) return;
        this.galleryInitialized = true;
        this.galleryConnected = true;
        this.lifecycleController =
          typeof AbortController === "undefined" ? null : new AbortController();
        this.pendingFrames = new Set();
        this.track = this.querySelector("[data-gallery-track]");
        this.allSlides = Array.from(
          this.querySelectorAll("[data-gallery-slide]"),
        );
        this.allThumbnails = Array.from(
          this.querySelectorAll("[data-gallery-thumbnail]"),
        );
        this.controls = this.querySelector("[data-gallery-controls]");
        this.previousButton = this.querySelector("[data-gallery-previous]");
        this.nextButton = this.querySelector("[data-gallery-next]");
        this.currentOutput = this.querySelector("[data-gallery-current]");
        this.totalOutput = this.querySelector("[data-gallery-total]");
        this.zoomButtons = Array.from(
          this.querySelectorAll("[data-gallery-zoom-open]"),
        );
        this.zoomDialog = this.querySelector("[data-gallery-zoom-dialog]");
        this.zoomImage = this.querySelector("[data-gallery-zoom-image]");
        this.zoomClose = this.querySelector("[data-gallery-zoom-close]");
        this.visibleSlides = [];
        this.visibleThumbnails = [];
        this.activeIndex = 0;

        const signal = this.lifecycleController?.signal;
        this.previousButton?.addEventListener(
          "click",
          () => this.showIndex(this.activeIndex - 1),
          { signal },
        );
        this.nextButton?.addEventListener(
          "click",
          () => this.showIndex(this.activeIndex + 1),
          { signal },
        );
        this.allThumbnails.forEach((thumbnail) => {
          thumbnail.addEventListener(
            "click",
            () => {
              const index = this.visibleThumbnails.indexOf(thumbnail);
              if (index >= 0) this.showIndex(index);
            },
            { signal },
          );
        });
        this.zoomButtons.forEach((button) => {
          button.addEventListener("click", () => this.openZoom(button), {
            signal,
          });
        });
        this.zoomClose?.addEventListener(
          "click",
          () => this.zoomDialog?.close(),
          { signal },
        );
        this.zoomDialog?.addEventListener(
          "click",
          (event) => {
            if (event.target === this.zoomDialog) this.zoomDialog.close();
          },
          { signal },
        );
        this.track?.addEventListener("scroll", () => this.queueScrollUpdate(), {
          passive: true,
          signal,
        });
        if (typeof window.addEventListener === "function") {
          window.addEventListener("resize", () => this.queueActiveHeight(), {
            passive: true,
            signal,
          });
          window.addEventListener(
            "orientationchange",
            () => this.queueActiveHeight(),
            { passive: true, signal },
          );
        }

        this.allSlides.forEach((slide) => {
          const image = slide.querySelector(".orthora-pdp__media-image");
          if (!image) return;
          image.addEventListener(
            "load",
            () => {
              if (this.isActiveSlide(slide)) this.queueActiveHeight();
            },
            { passive: true, signal },
          );
          if (typeof image.decode === "function") {
            image
              .decode()
              .catch(() => undefined)
              .then(() => {
                if (!signal.aborted && this.isActiveSlide(slide)) {
                  this.queueActiveHeight();
                }
              });
          }
        });

        if (this.track && typeof ResizeObserver !== "undefined") {
          this.resizeObserver = new ResizeObserver(() =>
            this.queueActiveHeight(),
          );
          this.resizeObserver.observe(this.track);
        }

        this.applyVariantMedia(this.dataset.selectedMediaId, {
          behavior: "auto",
          scroll: false,
        });
        this.queueActiveHeight();
      }

      disconnectedCallback() {
        this.galleryInitialized = false;
        this.galleryConnected = false;
        this.lifecycleController?.abort();
        this.resizeObserver?.disconnect();
        this.resizeObserver = null;
        if (typeof cancelAnimationFrame === "function") {
          if (this.heightFrame) cancelAnimationFrame(this.heightFrame);
          if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
          this.pendingFrames?.forEach((frame) => cancelAnimationFrame(frame));
        }
        this.pendingFrames?.clear();
        if (this.variantTimer && typeof window.clearTimeout === "function") {
          window.clearTimeout(this.variantTimer);
        }
        this.heightFrame = null;
        this.scrollFrame = null;
        this.variantTimer = null;
        this.programmaticTargetMediaId = "";
        this.snapRestoreBound = false;
        this.variantScrollSnapType = undefined;
      }

      scheduleFrame(callback) {
        let frame;
        let completed = false;
        frame = requestAnimationFrame(() => {
          completed = true;
          if (frame) this.pendingFrames?.delete(frame);
          if (this.galleryConnected) callback();
        });
        if (!completed && frame) this.pendingFrames?.add(frame);
        return frame;
      }

      queueActiveHeight() {
        if (!this.galleryConnected || this.heightFrame) return;
        this.heightFrame = -1;
        const frame = requestAnimationFrame(() => {
          this.heightFrame = null;
          if (this.galleryConnected) this.syncActiveHeight();
        });
        if (this.heightFrame !== null) this.heightFrame = frame;
      }

      syncActiveHeight() {
        if (!this.track) return;
        const activeSlide = this.visibleSlides[this.activeIndex];
        if (!activeSlide) {
          this.track.style.height = "";
          return;
        }
        const mediaWidth = Number(activeSlide.dataset.mediaWidth);
        const mediaHeight = Number(activeSlide.dataset.mediaHeight);
        if (!(mediaWidth > 0 && mediaHeight > 0)) {
          this.track.style.height = "";
          return;
        }
        const style = getComputedStyle(activeSlide);
        const horizontal =
          this.pixelValue(style.paddingLeft) +
          this.pixelValue(style.paddingRight) +
          this.pixelValue(style.borderLeftWidth) +
          this.pixelValue(style.borderRightWidth);
        const vertical =
          this.pixelValue(style.paddingTop) +
          this.pixelValue(style.paddingBottom) +
          this.pixelValue(style.borderTopWidth) +
          this.pixelValue(style.borderBottomWidth);
        const outerWidth =
          activeSlide.getBoundingClientRect().width || this.track.clientWidth;
        const contentWidth = Math.max(0, outerWidth - horizontal);
        const outerHeight =
          contentWidth * (mediaHeight / mediaWidth) + vertical;
        if (Number.isFinite(outerHeight) && outerHeight > 0) {
          this.track.style.height = `${outerHeight}px`;
        }
      }

      pixelValue(value) {
        const parsed = Number.parseFloat(value);
        return Number.isFinite(parsed) ? parsed : 0;
      }

      isActiveSlide(slide) {
        return this.visibleSlides[this.activeIndex] === slide;
      }

      openZoom(button) {
        const slide = button.closest("[data-gallery-slide]");
        const image = slide?.querySelector(".orthora-pdp__media-image");
        if (!image || !this.zoomDialog || !this.zoomImage) return;
        const width = Number(
          button.dataset.mediaWidth || slide.dataset.mediaWidth,
        );
        const height = Number(
          button.dataset.mediaHeight || slide.dataset.mediaHeight,
        );
        this.zoomImage.src = image.currentSrc || image.src;
        this.zoomImage.alt = image.alt || this.dataset.productTitle || "";
        if (width > 0 && height > 0) {
          this.zoomImage.width = width;
          this.zoomImage.height = height;
        }
        this.zoomDialog.showModal();
      }

      queueScrollUpdate() {
        if (this.scrollFrame || this.visibleSlides.length === 0) return;
        this.scrollFrame = -1;
        const frame = requestAnimationFrame(() => {
          this.scrollFrame = null;
          if (!this.galleryConnected || !this.track) return;
          if (this.programmaticTargetMediaId) {
            const targetIndex = this.visibleSlides.findIndex(
              (slide) =>
                slide.dataset.mediaId === this.programmaticTargetMediaId,
            );
            const targetSlide = this.visibleSlides[targetIndex];
            if (
              targetSlide &&
              Math.abs(
                this.relativeSlideOffset(targetSlide) - this.track.scrollLeft,
              ) > 1
            ) {
              return;
            }
            this.programmaticTargetMediaId = "";
            if (targetIndex >= 0) {
              this.updateState(targetIndex);
              return;
            }
          }
          const distances = this.visibleSlides.map((slide) =>
            Math.abs(this.relativeSlideOffset(slide) - this.track.scrollLeft),
          );
          const closestDistance = Math.min(...distances);
          this.updateState(distances.indexOf(closestDistance));
        });
        if (this.scrollFrame !== null) this.scrollFrame = frame;
      }

      showIndex(nextIndex, behavior = "smooth") {
        if (this.visibleSlides.length === 0) return;
        const boundedIndex = Math.max(
          0,
          Math.min(nextIndex, this.visibleSlides.length - 1),
        );
        this.updateState(boundedIndex);
        this.scrollToSlide(this.visibleSlides[boundedIndex], behavior);
      }

      relativeSlideOffset(slide) {
        if (!slide || !this.track) return 0;
        const trackOffset =
          slide.offsetParent === this.track ? 0 : this.track.offsetLeft;
        const relativeOffset = slide.offsetLeft - trackOffset;
        if (Number.isFinite(relativeOffset) && relativeOffset >= 0) {
          return relativeOffset;
        }
        const index = Math.max(0, this.visibleSlides.indexOf(slide));
        return index * this.track.clientWidth;
      }

      scrollToSlide(slide, behavior) {
        if (!slide || !this.track) return;
        this.programmaticTargetMediaId = slide.dataset.mediaId || "";
        const targetLeft =
          this.visibleSlides.indexOf(slide) === 0
            ? 0
            : this.relativeSlideOffset(slide);
        if (targetLeft === 0) {
          const scrollSnapType = this.track.style.scrollSnapType;
          const slideSnapTypes = this.allSlides.map(
            (gallerySlide) => gallerySlide.style.scrollSnapAlign,
          );
          this.track.style.scrollSnapType = "none";
          this.allSlides.forEach((gallerySlide) => {
            gallerySlide.style.scrollSnapAlign = "none";
          });
          slide.style.scrollSnapAlign = "start";
          this.track.scrollLeft = 0;
          this.scheduleFrame(() => {
            this.track.scrollLeft = 0;
            this.scheduleFrame(() => {
              this.allSlides.forEach((gallerySlide, index) => {
                gallerySlide.style.scrollSnapAlign = slideSnapTypes[index];
              });
              this.track.style.scrollSnapType = scrollSnapType;
              this.track.scrollTo({ behavior, left: 0 });
              this.programmaticTargetMediaId = "";
            });
          });
          return;
        }
        this.track.scrollTo({ behavior, left: targetLeft });
      }

      selectMedia(mediaId, selectedOptions = []) {
        const selectedMediaId = this.resolveMappedMediaId(
          mediaId,
          selectedOptions,
        );
        const activeMediaId =
          this.visibleSlides?.[this.activeIndex]?.dataset.mediaId || "";
        const shouldScroll =
          selectedMediaId !== this.selectedMediaId ||
          (selectedMediaId && activeMediaId !== selectedMediaId);
        this.applyVariantMedia(selectedMediaId, {
          behavior: "smooth",
          scroll: shouldScroll,
        });
      }

      applyVariantMedia(mediaId, { behavior, scroll }) {
        this.selectedMediaId = mediaId == null ? "" : String(mediaId);
        const sourceSlides = this.sourceOrderedItems(this.allSlides);
        const sourceThumbnails = this.sourceOrderedItems(this.allThumbnails);
        const commonSlides = sourceSlides.filter(
          (slide) => this.variantMediaId(slide) === "",
        );
        const selectedSlide = sourceSlides.find(
          (slide) =>
            this.selectedMediaId &&
            this.variantMediaId(slide) === this.selectedMediaId,
        );
        const commonThumbnails = sourceThumbnails.filter(
          (thumbnail) => this.variantMediaId(thumbnail) === "",
        );
        const selectedThumbnail = sourceThumbnails.find(
          (thumbnail) =>
            this.selectedMediaId &&
            this.variantMediaId(thumbnail) === this.selectedMediaId,
        );

        this.visibleSlides = selectedSlide
          ? [selectedSlide, ...commonSlides]
          : commonSlides;
        this.visibleThumbnails = selectedThumbnail
          ? [selectedThumbnail, ...commonThumbnails]
          : commonThumbnails;

        const trackDisplay = this.track?.style.display || "";
        if (scroll && this.track) {
          this.track.style.display = "none";
          void this.track.offsetWidth;
        }
        this.syncVisibility(this.allSlides, this.visibleSlides);
        this.syncVisibility(this.allThumbnails, this.visibleThumbnails);
        this.syncDomOrder(this.allSlides, this.visibleSlides);
        this.syncDomOrder(this.allThumbnails, this.visibleThumbnails);
        if (scroll && this.track) {
          this.track.scrollLeft = 0;
          this.track.style.display = trackDisplay;
          void this.track.offsetWidth;
          this.track.scrollLeft = 0;
        }
        this.updateState(0);
        if (scroll && this.track && this.visibleSlides[0]) {
          this.scrollToSlide(this.visibleSlides[0], behavior);
          const selectedMediaId = this.selectedMediaId;
          if (this.variantTimer && typeof window.clearTimeout === "function") {
            window.clearTimeout(this.variantTimer);
          }
          this.variantTimer = window.setTimeout(() => {
            this.variantTimer = null;
            if (
              !this.galleryConnected ||
              this.selectedMediaId !== selectedMediaId ||
              !this.track
            ) {
              return;
            }
            if (this.variantScrollSnapType === undefined) {
              this.variantScrollSnapType = this.track.style.scrollSnapType;
            }
            this.track.style.scrollSnapType = "none";
            this.track.scrollLeft = 0;
            this.updateState(0);
            if (!this.snapRestoreBound) {
              this.snapRestoreBound = true;
              const restoreSnap = () => {
                if (this.track) {
                  this.track.style.scrollSnapType = this.variantScrollSnapType;
                }
                this.snapRestoreBound = false;
              };
              const listenerOptions = {
                once: true,
                passive: true,
                signal: this.lifecycleController?.signal,
              };
              this.track.addEventListener(
                "pointerdown",
                restoreSnap,
                listenerOptions,
              );
              this.track.addEventListener(
                "touchstart",
                restoreSnap,
                listenerOptions,
              );
            }
          }, 120);
        }
      }

      syncVisibility(allItems, visibleItems) {
        const visibleSet = new Set(visibleItems);
        allItems.forEach((item) => {
          const visibleIndex = visibleItems.indexOf(item);
          const isVisible = visibleSet.has(item);
          item.hidden = !isVisible;
          item.style.display = isVisible ? "" : "none";
          item.style.order = String(isVisible ? visibleIndex : allItems.length);
          item.dataset.slideIndex = isVisible ? String(visibleIndex + 1) : "";
          if (item.matches?.("[data-gallery-slide]")) {
            item
              .querySelector("[data-gallery-zoom-open]")
              ?.setAttribute(
                "aria-label",
                isVisible
                  ? `Open media ${visibleIndex + 1} in modal`
                  : "Hidden variant media",
              );
          }
          if (item.matches?.("[data-gallery-thumbnail]")) {
            item.setAttribute(
              "aria-label",
              isVisible
                ? `View media ${visibleIndex + 1}`
                : "Hidden variant media",
            );
          }
        });
      }

      syncDomOrder(allItems, visibleItems) {
        const parent = allItems[0]?.parentElement;
        if (!parent?.append) return;
        const visibleSet = new Set(visibleItems);
        [
          ...visibleItems,
          ...allItems.filter((item) => !visibleSet.has(item)),
        ].forEach((item) => parent.append(item));
      }

      variantMediaId(item) {
        const liquidMediaId = String(item.dataset.variantMedia || "").trim();
        if (liquidMediaId) return liquidMediaId;
        const config = this.productMediaConfig();
        if (!config?.f) return "";
        const filename = this.normalizedFilename(item.dataset.filename);
        const isVariantMedia = Object.values(config.f).some(
          (variantFilename) =>
            this.normalizedFilename(variantFilename) === filename,
        );
        return isVariantMedia ? String(item.dataset.mediaId || "") : "";
      }

      productMediaConfig() {
        const handle = String(this.dataset.productHandle || "");
        return window.OrthoraProductMediaMap?.p?.[handle] || null;
      }

      normalizedFilename(value) {
        let filename = String(value || "").trim().split(/[?#]/u, 1)[0];
        filename = filename.split("/").pop() || "";
        try {
          filename = decodeURIComponent(filename);
        } catch (_error) {
          // Keep the Shopify filename when a legacy percent escape is invalid.
        }
        const uuid =
          /_?[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/giu;
        return filename
          .toLowerCase()
          .replace(uuid, "")
          .replace(/_+(?=\.)/gu, "")
          .replace(/_{2,}/gu, "_");
      }

      sourceOrderedItems(items) {
        const sourceOrder = this.productMediaConfig()?.m;
        if (!Array.isArray(sourceOrder) || sourceOrder.length === 0) {
          return [...items];
        }
        const orderByFilename = new Map(
          sourceOrder.map((filename, index) => [
            this.normalizedFilename(filename),
            index,
          ]),
        );
        return [...items]
          .map((item, index) => ({
            index,
            item,
            order:
              orderByFilename.get(
                this.normalizedFilename(item.dataset.filename),
              ) ?? Number.MAX_SAFE_INTEGER,
          }))
          .sort((left, right) =>
            left.order === right.order
              ? left.index - right.index
              : left.order - right.order,
          )
          .map(({ item }) => item);
      }

      resolveMappedMediaId(mediaId, selectedOptions) {
        const explicitMediaId = mediaId == null ? "" : String(mediaId);
        if (explicitMediaId) return explicitMediaId;
        const config = this.productMediaConfig();
        if (!config?.f || !Array.isArray(selectedOptions)) return "";
        const optionKey = selectedOptions
          .map((option) => String(option).trim())
          .join("\u001f");
        const mappedFilename = config.f[optionKey];
        if (!mappedFilename) return "";
        const normalizedMappedFilename = this.normalizedFilename(mappedFilename);
        const mappedSlide = this.allSlides.find(
          (slide) =>
            this.normalizedFilename(slide.dataset.filename) ===
            normalizedMappedFilename,
        );
        return String(mappedSlide?.dataset.mediaId || "");
      }

      updateState(nextIndex) {
        const total = this.visibleSlides.length;
        const boundedIndex = total
          ? Math.max(0, Math.min(nextIndex, total - 1))
          : 0;
        this.activeIndex = boundedIndex;
        if (this.currentOutput) {
          this.currentOutput.textContent = total
            ? String(boundedIndex + 1)
            : "0";
        }
        if (this.totalOutput) this.totalOutput.textContent = String(total);
        if (this.previousButton) {
          this.previousButton.disabled = total <= 1 || boundedIndex === 0;
        }
        if (this.nextButton) {
          this.nextButton.disabled = total <= 1 || boundedIndex === total - 1;
        }
        if (this.controls) {
          const controlsHidden = total <= 1;
          this.controls.hidden = controlsHidden;
          this.controls.setAttribute("aria-hidden", String(controlsHidden));
        }

        this.allSlides.forEach((slide) => {
          const visibleIndex = this.visibleSlides.indexOf(slide);
          const isActive = visibleIndex === boundedIndex && visibleIndex >= 0;
          slide.setAttribute("aria-hidden", String(!isActive));
          slide.inert = !isActive;
        });
        this.allThumbnails.forEach((thumbnail) => {
          const visibleIndex = this.visibleThumbnails.indexOf(thumbnail);
          const isActive = visibleIndex === boundedIndex && visibleIndex >= 0;
          thumbnail.classList.toggle("is-active", isActive);
          thumbnail.setAttribute("aria-current", String(isActive));
        });

        const activeThumbnail = this.visibleThumbnails[boundedIndex];
        const thumbnailTrack = activeThumbnail?.parentElement;
        if (activeThumbnail && thumbnailTrack?.scrollTo) {
          thumbnailTrack.scrollTo({
            behavior: "smooth",
            left: activeThumbnail.offsetLeft,
          });
        }
        this.queueActiveHeight();
      }
    },
  );
}
